// Centralized GraphQL fetch function
const SHOPIFY_STOREFRONT_API_URL = 'https://848b36-83.myshopify.com//api/2025-01/graphql.json';
const STOREFRONT_ACCESS_TOKEN = 'bcad781e786bbe15aa1bebada86254b6';

async function fetchGraphQL(query, variables = {}, language = null) {
  const headers = {
    'Content-Type': 'application/json',
    'X-Shopify-Storefront-Access-Token': STOREFRONT_ACCESS_TOKEN

  };
  if (language) {
    headers['Accept-Language'] = language;
  }

  try {
    const response = await fetch(SHOPIFY_STOREFRONT_API_URL, {
      method: 'POST',
      headers,
      body: JSON.stringify({ query, variables })
    });

    const result = await response.json();
    if (result.errors) {
      console.error('GraphQL Errors:', result.errors);
      throw new Error('GraphQL query failed');
    }
    return result.data;
  } catch (error) {
    console.error('Fetch Error:', error);
    throw error;
  }
}

// Utility to parse JSON safely and ensure an array output
function parseJsonSafe(value, fallback = []) {
  if (!value) return fallback; // Handle undefined or null

  try {
    const parsed = JSON.parse(value);
    // Normalize to array: if it's already an array, return it; otherwise, wrap it in an array
    return Array.isArray(parsed) ? parsed : [parsed];
  } catch (error) {
    console.error('Error parsing JSON:', error, value);

    // If parsing fails but value is a valid string, return it as an array
    return typeof value === 'string' && value.trim() ? [value.trim()] : fallback;
  }
}

// Utility function to decode &quot; to actual quotes
function decodeQuotes(data) {
  return data.replace(/&quot;/g, '"');
}

// Fetch all unique brands and their models
async function fetchAllProductsBrandsAndModels(collectionId, shopLanguage) {
  const brandModelMap = new Map();
  let cursor = null;

  const query = `
    query Products($id: ID!, $cursor: String) {
      collection(id: $id) {
        products(first: 250, after: $cursor, sortKey: ID) {
          edges {
            node {
              publishedAt
              brand: metafield(namespace: "is_hidden", key: "marca_dispozitiv") {
                value
              }
              models: metafield(namespace: "is_hidden", key: "model_dispozitiv") {
                value
              }
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    }
  `;

  while (true) {
    const variables = cursor ? { id: collectionId, cursor } : { id: collectionId };
    const data = await fetchGraphQL(query, variables, shopLanguage);

    if (!data?.collection?.products?.edges.length) {
      console.warn('No more products found or invalid collection ID.');
      break;
    }

    data.collection.products.edges
      .filter(edge => edge.node.publishedAt)
      .forEach(edge => {
        const brands = parseJsonSafe(edge.node.brand?.value, []);
        const models = parseJsonSafe(edge.node.models?.value, []);

        brands.forEach(brand => {
          const brandTrimmed = brand.trim();
          if (!brandModelMap[brandTrimmed]) {
            brandModelMap[brandTrimmed] = {};
          }

          models.forEach(model => {
            if (model) {
              const modelTrimmed = model.trim();
              // If the model already exists for the brand, increment its count
              if (brandModelMap[brandTrimmed][modelTrimmed]) {
                brandModelMap[brandTrimmed][modelTrimmed][1]++;
              } else {
                // Otherwise, initialize the model with a count of 1
                brandModelMap[brandTrimmed][modelTrimmed] = [modelTrimmed, 1];
              }
            }
          });
        });
      });

    const pageInfo = data.collection.products.pageInfo;
    if (!pageInfo.hasNextPage) break;

    cursor = pageInfo.endCursor;
    await new Promise(resolve => setTimeout(resolve, 750));
  }

  // Convert Map to Object
  const result = {};
  for (const brand of Object.keys(brandModelMap).sort()) {
    const modelsMap = brandModelMap[brand];
    result[brand] = Object.values(modelsMap);
  }

  return result;
}

async function fetchActiveDiscounts(cartId) {
  const query = `
    query cartDiscounts($id: ID!) {
      cart(id: $id) {
        discountApplications(first: 10) {
          edges {
            node {
              ... on AutomaticDiscountApplication {
                title
                value {
                  ... on PricingPercentageValue {
                    percentage
                  }
                  ... on PricingAmountValue {
                    amount {
                      amount
                      currencyCode
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  `;

  const variables = { id: cartId };
  const data = await fetchGraphQL(query, variables);

  if (!data?.cart?.discountApplications?.edges) return [];

  // Flatten the discount nodes
  const discounts = data.cart.discountApplications.edges.map(edge => edge.node);
  return discounts;
}

async function fetchAllProductsModels(collectionHandle) {
  let uniqueModels = new Set();
  let cursor = null;

  const query = `
    query Products($cursor: String) {
      collectionByHandle(handle: "${collectionHandle}") {
        products(first: 250, after: $cursor, sortKey: ID) {
          edges {
            node {
              publishedAt
              metafield(namespace: "is_hidden", key: "model_dispozitiv") {
                value
              }
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    }
  `;

  while (true) {
    const variables = cursor ? { cursor } : {};
    const data = await fetchGraphQL(query, variables);

    if (!data?.collectionByHandle?.products?.edges.length) {
      console.warn('No more products found.');
      break;
    }

    const collection = data.collectionByHandle;
    collection.products.edges.forEach(edge => {
      if (!edge.node.publishedAt) return;

      const models = edge.node.metafield?.value;
      if (models) {
        const modelArray = parseJsonSafe(models);
        modelArray.forEach(model => uniqueModels.add(model.trim()));
      }
    });

    const pageInfo = collection.products.pageInfo;
    if (!pageInfo.hasNextPage) break;

    cursor = pageInfo.endCursor;
    await new Promise(resolve => setTimeout(resolve, 750));
  }

  return Array.from(uniqueModels).sort();
}

async function fetchModelsByBrand(collectionHandle, collectionId, brandValue) {
  const normalizedBrand = brandValue.trim().toLowerCase();
  const cacheKey = `models_${collectionHandle}_${normalizedBrand}`;
  const cachedModels = sessionStorage.getItem(cacheKey);
  if (cachedModels) {
    return JSON.parse(cachedModels);
  }

  const query = `
    query Products($collectionId: ID!, $cursor: String) {
      collection(id: $collectionId) {
        products(first: 250, after: $cursor) {
          edges {
            node {
              brand: metafield(namespace: "is_hidden", key: "marca_dispozitiv") {
                value
              }
              models: metafield(namespace: "is_hidden", key: "model_dispozitiv") {
                value
              }
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    }
  `;

  const modelCount = new Map();
  const fetchPage = async (cursor, retries = 3) => {
    const variables = {
      collectionId: collectionId,
      ...(cursor && { cursor }) // Only add cursor if it exists
    };
    try {
      const data = await fetchGraphQL(query, variables);
      const collection = data?.collection;

      if (!collection?.products?.edges.length) return null;

      collection.products.edges.forEach(edge => {
        const brand = edge.node.brand?.value;
        const models = edge.node.models?.value;

        if (brand) {
          const brandArray = parseJsonSafe(brand);
          const matchesBrand = Array.isArray(brandArray)
            ? brandArray.some(b => b.trim().toLowerCase() === normalizedBrand)
            : brand.trim().toLowerCase() === normalizedBrand;

          if (matchesBrand && models) {
            const modelArray = parseJsonSafe(models);
            modelArray.forEach(model => {
              const modelName = model.trim();
              modelCount.set(modelName, (modelCount.get(modelName) || 0) + 1);
            });
          }
        }
      });

      return collection.products.pageInfo;
    } catch (err) {
      if (retries > 0 && err.status !== 430) {
        await new Promise(resolve => setTimeout(resolve, 500 * (4 - retries))); // Exponential backoff
        return fetchPage(cursor, retries - 1);
      }
      console.error('Fetch failed:', err);
      return null;
    }
  };

  let cursors = [null];
  const maxConcurrent = 8; // Storefront API has no strict limit
  const allPromises = [];

  while (cursors.length > 0) {
    const batch = cursors.splice(0, maxConcurrent);
    const batchPromises = batch.map(cursor => fetchPage(cursor));
    allPromises.push(...batchPromises);

    const results = await Promise.all(batchPromises);
    results.forEach(pageInfo => {
      if (pageInfo?.hasNextPage) cursors.push(pageInfo.endCursor);
    });
  }

  await Promise.all(allPromises); // Ensure all pages are processed

  const sortedModels = Array.from(modelCount.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  sessionStorage.setItem(cacheKey, JSON.stringify(sortedModels));
  return sortedModels;
}

async function fetchModelsBySearchTermAndBrand(searchTerm, brandValue) {
  const normalizedBrand = brandValue.trim().toLowerCase();
  const normalizedSearchTerm = (searchTerm || '').trim().toLowerCase();
  const cacheKey = `search_models_${normalizedSearchTerm}_${normalizedBrand}`;
  const cachedModels = sessionStorage.getItem(cacheKey);

  if (cachedModels) {
    return JSON.parse(cachedModels);
  }

  const query = `
    query SearchProducts($query: String!, $cursor: String) {
      search(query: $query, types: PRODUCT, first: 250, after: $cursor) {
        edges {
          node {
            ... on Product {
              publishedAt
              brand: metafield(namespace: "is_hidden", key: "marca_dispozitiv") {
                value
              }
              models: metafield(namespace: "is_hidden", key: "model_dispozitiv") {
                value
              }
            }
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  `;

  const modelCount = new Map();
  let cursor = null;

  while (true) {
    const variables = cursor ? { query: searchTerm, cursor } : { query: searchTerm };
    const data = await fetchGraphQL(query, variables);
    const results = data?.search;

    if (!results?.edges?.length) break;

    results.edges.forEach(({ node }) => {
      if (!node?.publishedAt) return;

      const brands = parseJsonSafe(node.brand?.value, []);
      const models = parseJsonSafe(node.models?.value, []);
      const matchesBrand = brands.some((brand) => brand.trim().toLowerCase() === normalizedBrand);

      if (!matchesBrand) return;

      models.forEach((model) => {
        const modelName = model.trim();
        if (!modelName) return;
        modelCount.set(modelName, (modelCount.get(modelName) || 0) + 1);
      });
    });

    if (!results.pageInfo?.hasNextPage) break;

    cursor = results.pageInfo.endCursor;
    await new Promise((resolve) => setTimeout(resolve, 500));
  }

  const sortedModels = Array.from(modelCount.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  sessionStorage.setItem(cacheKey, JSON.stringify(sortedModels));
  return sortedModels;
}


window.vendorDataCache = window.vendorDataCache || {};

/**
 * @returns {{ models: [string, number][], productTypesLocal: [string, number][], productTypesRo: [string, number][] }}
 * `productTypesLocal` — `productType` in `shopLanguage` (labels + filter URLs).
 * `productTypesRo` — same in `productTypeLabelLocale` (e.g. `ro` for icon slug / metaobject match). Identical to Local when locales match.
 */
async function fetchModelAndProductTypesByVendor(
  vendorValue,
  shopLanguage,
  productTypeLabelLocale
) {
  const ptRoLocale = productTypeLabelLocale || shopLanguage;
  const cacheKey = [vendorValue, shopLanguage, ptRoLocale].join('|');
  if (window.vendorDataCache[cacheKey]) {
    return window.vendorDataCache[cacheKey];
  }

  async function fetchProductsByLanguage(language, vendorValue) {
    let cursor = null;
    const productTypeCount = new Map();
    const modelCount = new Map();

    const query = `
      query ProductsByVendor($cursor: String) {
        products(first: 250, query: "vendor:${vendorValue}", after: $cursor, sortKey: ID) {
          edges {
            node {
              publishedAt
              vendor
              productType
              models: metafield(namespace: "is_hidden", key: "model_dispozitiv") {
                value
              }
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    `;

    while (true) {
      const variables = cursor ? { cursor } : {};
      const data = await fetchGraphQL(query, variables, language);

      if (!data?.products?.edges.length) {
        console.warn(`No more products found for vendor (${language}):`, vendorValue);
        break;
      }

      const products = data.products;
      products.edges.forEach(edge => {
        if (!edge.node.publishedAt) return;

        const vendor = edge.node.vendor;
        const models = edge.node.models?.value;
        const productType = edge.node.productType;

        const matchesVendor = vendor.trim().toLowerCase() === vendorValue.trim().toLowerCase();

        if (matchesVendor) {
          if (models && language === shopLanguage) {
            const modelArray = parseJsonSafe(models);
            modelArray.forEach(model => {
              const modelName = model.trim();
              modelCount.set(modelName, (modelCount.get(modelName) || 0) + 1);
            });
          }

          if (productType) {
            const typeName = productType.trim();
            productTypeCount.set(typeName, (productTypeCount.get(typeName) || 0) + 1);
          }
        }
      });

      const pageInfo = products.pageInfo;
      if (!pageInfo.hasNextPage) break;

      cursor = pageInfo.endCursor;
      await new Promise(resolve => setTimeout(resolve, 750));
    }

    return { productTypeCount, modelCount };
  }

  const localData = await fetchProductsByLanguage(shopLanguage, vendorValue);
  const productTypesLocal = Array.from(localData.productTypeCount.entries());

  let productTypesRo;
  if (ptRoLocale === shopLanguage) {
    productTypesRo = productTypesLocal;
  } else {
    const roData = await fetchProductsByLanguage(ptRoLocale, vendorValue);
    productTypesRo = Array.from(roData.productTypeCount.entries());
  }

  const sortedModels = Array.from(localData.modelCount.entries()).sort((a, b) => a[0].localeCompare(b[0]));

  window.vendorDataCache[cacheKey] = {
    models: sortedModels,
    productTypesLocal,
    productTypesRo
  };

  return window.vendorDataCache[cacheKey];
}

async function fetchVariantsCollectionId(productId) {
  const query = `
    query ($productId: ID!) {
      product(id: $productId) {
        collections(first: 10) {
          edges {
            node {
              id
              title
              handle
              is_query_variants: metafield(namespace: "collection_custom", key: "query_variants") {
                value
              }
            }
          }
        }
      }
    }
  `;

  const variables = { productId: `gid://shopify/Product/${productId}` };

  try {
    const data = await fetchGraphQL(query, variables);
    if (!data?.product?.collections?.edges) {
      console.warn(`No collections found for product ID: ${productId}`);
      return null;
    }

    // Find the first collection active for query variants
    const variantsCollection = data.product.collections.edges.find(edge => {
      const is_query_variants = edge.node.is_query_variants || [];
      return parseJsonSafe(is_query_variants?.value)[0] === true;
    });

    if (!variantsCollection) {
      console.warn(`No child collection found for product ID: ${productId}`);
      return null;
    }

    return variantsCollection.node.id; // Returns full GID, e.g., "gid://shopify/Collection/123456789"
  } catch (error) {
    console.error('Failed to fetch child collection ID:', error);
    throw error;
  }
}

async function fetchProductVariants(collectionId, familyIdKeys, familyIdValues, desiredVendor, shopLanguage) {
  const allProducts = [];
  const foundValues = new Set();
  let hasNextPage = true;
  let cursor = null;
  let pageCount = 0;

  // Generate a list of metafield identifiers for all namespaces and keys
  const metafieldIdentifiers = [
    ...familyIdKeys.map(key => `{ namespace: "family_id", key: "${key}" }`),
    ...familyIdKeys.map(key => `{ namespace: "is_hidden", key: "${key}" }`),
    ...familyIdKeys.map(key => `{ namespace: "prezentare", key: "${key}" }`),
  ].join(', ');

  const query = `
    query ($collectionId: ID!, $cursor: String) {
      collection(id: $collectionId) {
        id
        title
        products(first: 250, after: $cursor, sortKey: ID) {
          edges {
            node {
              id
              handle
              title
              vendor
              featuredImage {
                url(transform: { maxWidth: 300 })
              }
              metafields(identifiers: [${metafieldIdentifiers}]) {
                namespace
                key
                value
              }
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    }
  `;

  while (hasNextPage) {
    const variables = { collectionId, cursor };
    try {
      pageCount++;
      const data = await fetchGraphQL(query, variables, shopLanguage);

      if (!data?.collection?.products?.edges) {
        console.warn(`No products found in collection ID: ${collectionId}`);
        return { id: collectionId, title: data?.collection?.title || 'Unknown', products: { edges: allProducts } };
      }

      const filteredProducts = data.collection.products.edges
        .map(edge => edge.node)
        .filter(product => {
          // vendor filter
          return product.vendor && product.vendor.toLowerCase() === desiredVendor.toLowerCase();
        })
        .map(product => {
          // Filter out null metafields (if any)
          const metafields = (product.metafields || []).filter(m => m !== null && m.value != null);
          return { node: { ...product, metafields } };
        })
        .filter(({ node }) => {
          const familyIdMetafields = node.metafields.filter(m => m?.namespace === 'family_id' && m?.value);
          // Ensure there’s at least one non-empty family_id metafield
          const hasNonEmptyFamilyId = familyIdMetafields.length > 0 && familyIdMetafields.some(m => m.value.trim() !== '');
          if (!hasNonEmptyFamilyId) return false;

          // Check if any family_id metafield value contains any value from familyIdValues
          const matches = familyIdMetafields.some(m => {
            let parsedVal;
            try {
              parsedVal = JSON.parse(m.value);
            } catch (e) {
              console.warn('Invalid JSON metafield:', m.value);
              return false;
            }
            const familyId = parsedVal.family_id;
            return familyIdValues.includes(Number(familyId));
          });
          return matches;
        });

      allProducts.push(...filteredProducts);

      hasNextPage = data.collection.products.pageInfo.hasNextPage;
      cursor = data.collection.products.pageInfo.endCursor;
    } catch (error) {
      console.error('Failed to fetch product variants page:', error);
      throw error;
    }
  }

  return {
    id: collectionId,
    products: { edges: allProducts },
  };
}

async function fetchMetaobjectModelsByBrand(brandValue) {
 
  const normalizedBrand = brandValue.trim().toLowerCase();

  const query = `
    query GetDeviceModels($cursor: String) {
      metaobjects(type: "device_models", first: 100, after: $cursor) {
        edges {
          node {
            name: field(key: "name") {
              value
            }
            manuals: field(key: "manuals") {
              references(first: 10) {
                edges {
                  node {
                    ... on GenericFile {
                      id
                      url
                    }
                  }
                }
              }
            }
            manualsProfessional: field(key: "manuals_professional") {
              references(first: 10) {
                edges {
                  node {
                    ... on GenericFile {
                      id
                      url
                    }
                  }
                }
              }
            }
            image: field(key: "image") {
              reference {
                ... on MediaImage {
                  image {
                    url
                  }
                }
              }
            }
            vendor: field(key: "vendor") {
              reference {
                ... on Metaobject {
                  fields {
                    key
                    value
                  }
                }
              }
            }
            repair_guides: field(key: "repair_guides") {
              references(first: 10) {
                edges {
                  node {
                    ... on Metaobject {                       
                      id
                      name: field(key: "name") {
                        value
                       }
                       thumbnail_image:field(key: "thumbnail_image") {
                             reference {
                            ... on MediaImage {
                              image {
                                url
                              }
                            }
                          }
                       }
                    }
                  } 
                }
              }
            }
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  `;

  let models = [];
  let cursor = null;
  let hasNextPage = true;

  while (hasNextPage) {
    const variables = { cursor };
    try {
      const data = await fetchGraphQL(query, variables);
      const metaobjects = data?.metaobjects;

      if (!metaobjects?.edges?.length) break;

      for (const edge of metaobjects.edges) {
        const model = edge.node;
        const modelName = model.name?.value?.trim();
        const vendorFields = model.vendor?.reference?.fields || [];
        const vendorName = vendorFields.find(f => f.key === 'name')?.value?.trim().toLowerCase();

        if (vendorName === normalizedBrand && modelName) {
          const manuals = model.manuals?.references?.edges?.map(edge => edge.node?.url).filter(Boolean) || [];
          const repairGuides = model?.repair_guides?.references?.edges?.map(edge => {
            const node = edge?.node;
            const id = node?.id || null;
            const name = node?.name?.value?.trim() || null;
            const image = node?.thumbnail_image?.reference?.image?.url || null;

            if (!id && !name && !image) return null; // skip empty entries

            return { id, name, image };
          }).filter(Boolean) || [];

          models.push({
            name: modelName,
            vendor: vendorName,
            image: model.image?.reference?.image?.url || null,
            manuals,
            repairGuides
          });
        }
      }

      hasNextPage = metaobjects.pageInfo.hasNextPage;
      cursor = metaobjects.pageInfo.endCursor;
    } catch (err) {
      console.error('Metaobject fetch error:', err);
      break;
    }
  }

  return models;
}

async function fetchRepairGuideById(repairGuideId, shopLanguage) {
  const query = `
  query GetRepairGuide($id: ID!) {
    metaobject(id: $id) {
      id
      type
      name: field(key: "name") {
        value
      }
      intro: field(key: "intro") {
        value
      }
      youtubeVideoLink: field(key: "youtube_video_link") {
        value
      }
      videoFile: field(key: "video_file") {
        reference {
          ... on Video {
            sources {
              mimeType
              url
            }
          }
        }
      }
      service_tools: field(key: "service_tools") {
        references(first: 20) {
          edges {
            node {
              ... on Product {
                id
                handle
                title
                vendor
                featuredImage {
                  url
                  altText
                }
                variants(first: 1) {
                  edges {
                    node {
                      id
                      price {
                        amount
                        currencyCode
                      }
                      compareAtPrice {
                        amount
                        currencyCode
                      }
                      quantityAvailable 
                    }
                  }
                }
              }
            }
          }
        }
      }
      replacement_devices: field(key: "replacement_devices") {
        references(first: 20) {
          edges {
            node {
              ... on Product {
                id
                handle
                title
                vendor
                featuredImage {
                  url
                  altText
                }
                variants(first: 1) {
                  edges {
                    node {
                      id
                      price {
                        amount
                        currencyCode
                      }
                      compareAtPrice {
                        amount
                        currencyCode
                      }
                      quantityAvailable 
                    }
                  }
                }
              }
            }
          }
        }
      }
      repair_guide_steps: field(key: "repair_guide_steps") {
        references(first: 20) {
          edges {
            node {
              ... on Metaobject {
                id
                type
                title: field(key: "step_title") {
                  value
                }
                description: field(key: "step_description") {
                  value
                }
                youtubeVideoLink: field(key: "youtube_video_link") {
                  value
                }
                videoFile: field(key: "video_file") {
                  reference {
                    ... on Video {
                      sources {
                        mimeType
                        url
                      }
                    }
                  }
                }
                images: field(key: "step_images") {
                  references(first: 10) {
                    edges {
                      node {
                        ... on MediaImage {
                          image {
                            url
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  `;

  const variables = { id: repairGuideId };

  try {
    const data = await fetchGraphQL(query, variables, shopLanguage);
    const guide = data?.metaobject;

    if (!guide || guide.type !== 'repair_guides') return null;

    const extractProducts = (field) => (
      field?.references?.edges?.map(edge => {
        const product = edge.node;
        const variant = product?.variants?.edges?.[0]?.node;
        return {
          id: product?.id,
          handle: product?.handle,
          title: product?.title || null,
          vendor: product?.vendor || null,
          url: `/products/${product?.handle}` || null,
          price: variant?.price?.amount || null,
          compareAtPrice: variant?.compareAtPrice?.amount || null,
          imageUrl: product?.featuredImage?.url || null,
          imageAlt: product?.featuredImage?.altText || null,
          quantityAvailable: variant?.quantityAvailable || 0,
          variantId: variant?.id,
          currency: variant?.price?.currencyCode || null,
        };
      }).filter(Boolean) || []
    );

    const steps = guide?.repair_guide_steps?.references?.edges?.map(edge => {
      const node = edge.node;
      const images = node?.images?.references?.edges?.map(imageEdge =>
        imageEdge?.node?.image?.url
      ).filter(Boolean) || [];

      // Extract YouTube or video file URL
      const stepYoutubeLink = node?.youtubeVideoLink?.value?.trim() || null;
      const stepVideoSources = node?.videoFile?.reference?.sources || [];
      const stepVideoFileUrl = stepVideoSources.length > 0 ? stepVideoSources[0].url : null;

      const stepVideo = getVideoType(stepYoutubeLink, stepVideoFileUrl);

      return {
        id: node?.id,
        title: node?.title?.value?.trim() || null,
        description: node?.description?.value?.trim() || null,
        images,
        stepVideo
      };
    }).filter(Boolean) || [];

    // Extract YouTube or video file URL
    const youtubeLink = guide?.youtubeVideoLink?.value?.trim() || null;
    const videoSources = guide?.videoFile?.reference?.sources || [];
    const videoFileUrl = videoSources.length > 0 ? videoSources[0].url : null;

    const video = getVideoType(youtubeLink, videoFileUrl);

    return {
      id: guide.id,
      name: guide.name?.value?.trim() || null,
      intro: guide.intro?.value?.trim() || null,
      serviceTools: extractProducts(guide.service_tools),
      replacementDevices: extractProducts(guide.replacement_devices),
      steps,
      video
    };
  } catch (error) {
    console.error('Error fetching repair guide:', error);
    return null;
  }
}

async function fetchProductsByGIDs(productGIDs,familyIdKeys, shopLanguage) {
  const allProducts = [];

  // Generate a list of metafield identifiers for all namespaces and keys
  const metafieldIdentifiers = [
    ...familyIdKeys.map(key => `{ namespace: "is_hidden", key: "${key}" }`),
    ...familyIdKeys.map(key => `{ namespace: "prezentare", key: "${key}" }`),
  ].join(', ');


  const query = `
    query getProductsByIds($ids: [ID!]!) {
      nodes(ids: $ids) {
        ... on Product {
          id
          title
          handle
          vendor
          featuredImage {
            url(transform: { maxWidth: 300 })
            altText
          }
          metafields(identifiers: [${metafieldIdentifiers}]) {
                namespace
                key
                value
              }
        }
      }
    }
  `;

  // Shopify only allows a max of 250 nodes per query
  const chunkSize = 100;
  for (let i = 0; i < productGIDs.length; i += chunkSize) {
    const chunk = productGIDs.slice(i, i + chunkSize);
    const variables = { ids: chunk };

    try {
      const data = await fetchGraphQL(query, variables, shopLanguage);
      if (!data?.nodes) {
        console.warn(`No nodes returned for chunk starting at index ${i}`);
        continue;
      }

      const validProducts = data.nodes
        .filter(p => p)
        .map(product => {
          const metafields = (product.metafields || []).filter(m => m !== null && m.value != null);
          return {
            ...product,
            metafields,
          };
        });

      allProducts.push(...validProducts);
    } catch (error) {
      console.error(`Error fetching products chunk at index ${i}:`, error);
      throw error;
    }
  }

  return {
    products: allProducts,
  };
}


function getVideoType(youtubeLink, videoFileUrl ) {

  let video = null;
  if (youtubeLink) {
    const youtubeIdMatch = youtubeLink.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]{11})/);
    const videoId = youtubeIdMatch ? youtubeIdMatch[1] : null;
    if (videoId) {
      video = {
        type: "youtube",
        id: videoId,
      };
    }
  } else if (videoFileUrl) {
    video = {
      type: "file",
      url: videoFileUrl,
    };
  }

  return video;
}

function capitalizeFirstLetter(str) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function renderRichText(node) {
  if (!node) return "";

  if (node.type === "root" && Array.isArray(node.children)) {
    return node.children.map(renderRichText).join("");
  }

  if (node.type === "heading") {
    const level = node.level || 4;
    const content = (node.children || []).map(renderRichText).join("");
    return `<h${level}>${content}</h${level}>`;
  }

  if (node.type === "paragraph") {
    const content = (node.children || []).map(renderRichText).join("");
    // Skip empty paragraphs
    if (content.trim() === "") return "";
    return `<p>${content}</p>`;
  }

  if (node.type === "text") {
    return node.value || "";
  }

  // Fallback
  return "";
}

async function loadSVG(url) {
  const res = await fetch(url);
  return await res.text();
}
